/**
 * SA-448's list mode, dressed to the wireframe's `.steplist` by SA-459
 * (spec §4.4): a 6px stripe cell in the executor's tone, `N Title` with a
 * current or done chip, then the meta line. The same row renders the
 * workflow page's Steps tab (spec §5), so the two lists are one component.
 */
import type { KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import type { ProcessOverviewVM, ProcessStepVM } from '@/utils/workflowOverview'
import { cn } from '@/utils'
import { LANDING_RING_ON_CARD } from '@/components/records/landingRing'
import { executorTone } from '../executorTone'
import { placeCells } from './grid'
import { accessibleName, rowMeta } from './mapText'
import { MAP_GRID } from './mapTypes'

export interface MapListProps {
  vm: ProcessOverviewVM
  currentStep: string | null
  visited: Set<string>
  /** SA-1037: the steps an exit jumped over on the record. */
  skipped?: ReadonlySet<string>
  /** SA-1328: the row a landing names. */
  ringStep?: string | null
  /** The map's roving focus: only this row is tabbable. Omit for a plain list, where every row is. */
  focusedId?: string | null
  /** D11: a link when given, else a button that reports `onSelect`. */
  href?: (id: string) => string
  onSelect?: (id: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLElement>, id: string) => void
  onFocus?: (id: string) => void
}

export function MapList({ vm, currentStep, visited, skipped, ringStep, focusedId, href, onSelect, onKeyDown, onFocus }: MapListProps) {
  const lanes = placeCells(vm, MAP_GRID.DEFAULT_COLUMNS).lanes
  return (
    <div className="space-y-3">
      {lanes.map((lane) => (
        <section key={lane.id} data-testid="process-lane" className="space-y-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{lane.label} · {lane.count} steps</h4>
          {vm.steps.filter((s) => s.lane === lane.id).map((s) => (
            <MapListRow key={s.id} step={s} current={s.id === currentStep} visited={visited.has(s.id)} skipped={skipped?.has(s.id) ?? false} ring={s.id === ringStep}
              tabIndex={focusedId === undefined || s.id === focusedId ? 0 : -1}
              href={href?.(s.id)} onSelect={onSelect} onKeyDown={onKeyDown} onFocus={onFocus} />
          ))}
        </section>
      ))}
    </div>
  )
}

export interface MapListRowProps {
  step: ProcessStepVM
  current: boolean
  visited: boolean
  skipped?: boolean
  ring?: boolean
  tabIndex: 0 | -1
  href?: string
  onSelect?: (id: string) => void
  onKeyDown?: (e: KeyboardEvent<HTMLElement>, id: string) => void
  onFocus?: (id: string) => void
}

/** ONE interactive element as the wrapper's direct child, as `MapCard` —
 * `ProcessMap.focusCard` finds it as `[data-step-id] > button, > a`. */
export function MapListRow({ step, current, visited, skipped = false, ring = false, tabIndex, href, onSelect, onKeyDown, onFocus }: MapListRowProps) {
  const passed = skipped && !current && !visited
  const tone = executorTone(step)
  const inner = (
    <>
      <span aria-hidden="true" className="self-stretch" style={tone.stripeStyle} />
      <span className="min-w-0 py-2.5 pr-3">
        <span className="flex flex-wrap items-center gap-2 font-semibold text-foreground">
          <span className="font-mono text-[11px] font-normal text-muted-foreground">{step.number}</span>
          <span>{step.title}</span>
          {current && (
            <span className="rounded-full bg-foreground px-2 text-[11.5px] font-medium leading-5 text-background">current</span>
          )}
          {visited && !current && (
            <span className="rounded-full bg-success/12 px-2 text-[11.5px] font-medium leading-5 text-success">done</span>
          )}
          {passed && (
            <span className="rounded-full bg-muted px-2 text-[11.5px] font-medium leading-5 text-muted-foreground">skipped</span>
          )}
        </span>
        <span className="block truncate text-xs font-normal text-muted-foreground">{rowMeta(step)}</span>
      </span>
    </>
  )
  const cls = cn(
    'grid w-full grid-cols-[6px_minmax(0,1fr)] gap-2.5 overflow-hidden rounded-lg border bg-card text-left text-sm outline-none hover:bg-accent/30 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    current ? 'border-primary shadow-[0_0_0_1px_var(--primary)]' : 'border-border',
    passed && 'border-dashed opacity-60 focus-visible:opacity-100',
    ring && LANDING_RING_ON_CARD,
  )
  const common = {
    className: cls,
    'aria-label': accessibleName(step, current, visited, passed),
    tabIndex,
    onKeyDown: onKeyDown ? (e: KeyboardEvent<HTMLElement>) => onKeyDown(e, step.id) : undefined,
    onFocus: onFocus ? () => onFocus(step.id) : undefined,
  }
  return (
    <div data-step-id={step.id} data-current={current ? 'true' : 'false'} data-visited={visited ? 'true' : 'false'} data-skipped={passed ? 'true' : 'false'} data-landing={ring ? 'true' : undefined} className="relative">
      {href ? (
        <Link to={href} draggable={false} {...common}>{inner}</Link>
      ) : (
        <button type="button" onClick={onSelect ? () => onSelect(step.id) : undefined} {...common}>{inner}</button>
      )}
    </div>
  )
}
